import frontpageImgUrl from "assets/vsme-report-frontpage.png"
import { useEffect, useRef } from "react"

/**
 * Raw CSS @page rules for paged.js.
 *
 * These MUST be injected as a runtime <style> tag, NOT imported through
 * Vite's CSS pipeline. Vite's PostCSS / Lightning CSS strips nested
 * margin at-rules (@bottom-left etc.) because they are part of the CSS
 * Paged Media draft spec and not understood by standard CSS processors.
 *
 * paged.js's removeStyles() collects all <style> elements from the
 * document and passes them to its Polisher, which DOES understand
 * @page margin at-rules and converts them into real DOM elements.
 */
const PAGED_CSS = `
@page {
  size: A4 portrait;
  margin: 16mm 0 20mm 0;
}

@page legacy {
  @bottom-left {
    content: counter(page) " av " counter(pages);
    font-family: Roboto, system-ui, -apple-system, sans-serif;
    font-size: 8pt;
    color: #7f7f7f;
    padding-left: 16mm;
  }
}

/* Running header: paged.js hides the element in the flow and clones it
   into the @top-center margin box on every page. When a new dimension
   section starts, the latest .dimension-running-header replaces the
   previous one, so each dimension's pages show the correct name. */
.dimension-running-header {
  position: running(dimensionHeader);
  font-size: 10pt;
  font-weight: 500;
  line-height: 0.9em;
  color: #000;
  padding-bottom: 16px;
  border-bottom: 1px solid #CCCCCC;
}

@page vsme-frontpage {
  margin: 0 0 0 0;
  background: url("${frontpageImgUrl}") no-repeat center;
  background-size: cover;
  color: white;
}

@page vsme-dimension-cover {
  margin: 0 0 0 0;
}

@page vsme-dimension {
  margin-top: 100pt;

  @top-center {
    content: element(dimensionHeader);
    vertical-align: bottom;
    text-align: left;
    padding: 0 16mm 40pt 16mm;
  }

  @bottom-left {
    content: counter(page);
    font-family: Roboto, system-ui, -apple-system, sans-serif;
    font-size: 10pt;
    color: #000;
    padding-right: 16mm;
    text-align: right;
    background-color: #F2F2F2;
  }
}
`

/**
 * Materialize CSS rules from the CSSOM into <style> tag textContent.
 *
 * In production builds, Chakra UI's emotion runtime injects CSS via
 * `sheet.insertRule()` for performance. This means the <style> tag's
 * `textContent` is empty, even though `tag.sheet.cssRules` contains all
 * the actual rules (e.g. `.css-cfrok1 { display: flex; ... }`).
 *
 * paged.js's `removeStyles()` collects <style> tags by reading their
 * `textContent`. When that's empty, paged.js gets nothing and the
 * emotion CSS is silently lost — breaking all Chakra flex layouts,
 * borders, gaps, etc.
 *
 * In dev mode, emotion writes CSS directly as textContent (for HMR),
 * so paged.js picks it up fine. This is why the report looks correct
 * locally but breaks in staging/production.
 *
 * This function must be called BEFORE paged.js runs. It finds any
 * <style> tags whose textContent is empty but whose CSSStyleSheet has
 * rules, and serializes those rules back into textContent so paged.js
 * can process them.
 */
function materializeInsertedRules(): void {
  const styleTags = document.querySelectorAll("style")
  for (const tag of styleTags) {
    // Skip tags that already have textContent — paged.js can read those fine.
    if (tag.textContent && tag.textContent.trim().length > 0) continue

    // Check whether the tag has rules inserted via sheet.insertRule().
    let sheet: CSSStyleSheet | null = null
    try {
      sheet = tag.sheet
    } catch {
      // Cross-origin or otherwise inaccessible — skip.
      continue
    }

    if (!sheet || sheet.cssRules.length === 0) continue

    // Serialize all CSSOM rules back into CSS text.
    const parts: string[] = []
    for (let i = 0; i < sheet.cssRules.length; i++) {
      parts.push(sheet.cssRules[i].cssText)
    }
    tag.textContent = parts.join("\n")
  }
}

/**
 * Hook that runs paged.js to paginate any report with page-numbered
 * footers rendered as part of the document itself.
 *
 * Uses the Previewer API from the main "pagedjs" entry point (which Vite
 * resolves via exports.import -> src/index.js). This is the exact same code
 * the paged.polyfill.js bundle uses internally, without the auto-run wrapper
 * and without the Vite-incompatible subpath import.
 *
 * When called with no arguments, preview() automatically:
 *  1. wrapContent() — snapshots body innerHTML into a <template>
 *  2. removeStyles() — collects all <style>/<link> tags from the document
 *  3. polisher.add() — processes the CSS, handling @page margin at-rules
 *  4. chunker.flow() — fragments content into A4-sized page boxes
 *
 * Once pagination completes, window.gotenberg is set so Gotenberg can
 * capture the fully-paginated DOM as a PDF.
 *
 * @param isReady — true once React has finished rendering all report content
 */
export function usePagedJS(isReady: boolean) {
  const hasRun = useRef(false)

  useEffect(() => {
    if (!isReady || hasRun.current) return
    hasRun.current = true

    // Materialize any CSSOM-only rules (emotion production mode) into
    // textContent so paged.js's removeStyles() can actually read them.
    materializeInsertedRules()

    // Inject @page CSS as a raw <style> so paged.js can find it.
    const styleEl = document.createElement("style")
    styleEl.textContent = PAGED_CSS
    document.head.appendChild(styleEl)

    // Dynamic import keeps pagedjs out of the initial bundle.
    // "pagedjs" resolves via the package's exports.import field (src/index.js),
    // which Vite can pre-bundle without issues.
    import("pagedjs")
      .then(({ Previewer }) => {
        const previewer = new Previewer()
        return previewer.preview()
      })
      .then((flow: { total: number }) => {
        console.info(`[usePagedJS] paged.js finished — ${flow.total} pages`)
        ;(window as Window & { gotenberg?: boolean }).gotenberg = true
      })
      .catch((err: unknown) => {
        console.error("[usePagedJS] paged.js failed:", err)
        // Signal Gotenberg anyway so the request doesn't hang forever.
        ;(window as Window & { gotenberg?: boolean }).gotenberg = true
      })
  }, [isReady])
}
